// Auto-generated from docs/architecture/platform-api.md — do not edit manually
const content = `
# Platform API Architecture

## Overview

The **Vortx Platform API** (\`apps/vortx-platform-api/\`) is the central authentication gateway and routing layer for the entire Vortx Flux platform. Every request from the frontend passes through the Platform API, which:

1. Verifies Firebase authentication tokens
2. Fetches the user's authorization context (company, product access, admin status) from the Accounts Service
3. Generates signed service tokens for downstream backend services
4. Proxies requests to the appropriate domain API based on the URL path
5. Enforces product-level access control (e.g., user cannot call Flux EV API without a Flux EV seat)

The Platform API itself is a Flask application deployed on Google Cloud Run.

**Production URL:** \`https://vortx-platform-api-202472576296.us-central1.run.app\`

---

## Architecture Diagram

\`\`\`
Frontend (React)
    |
    | HTTPS + Firebase ID Token
    v
Platform API (Flask, Cloud Run)
    |
    |--- verify_token() -----> Firebase Auth (token verification)
    |--- get_auth_context() --> Accounts Service (user/company/products)
    |--- generate_service_token() --> JWT (HS256, 5-min TTL)
    |
    |--- /api/site_dna/*   --> Site DNA API (optimization, projects, scenarios)
    |--- /api/flux_ev/*    --> Flux EV API (EV fleet optimization)
    |--- /api/flux_phd/*   --> Flux PhD API (research/coordination)
    |--- /api/flux_ems/*   --> Flux EMS API (annual simulation engine)
    |--- /api/cold_storage/* -> Flux COLD Storage API
    |--- /api/v1/admin/*   --> Accounts Service (companies, users, seats)
    |                       --> Site DNA API (tariffs, equipment, solver monitoring)
    |--- /api/v1/billing/* --> Accounts Service (Stripe billing)
    |--- /api/simulation/* --> Flux EMS API (legacy simulation route)
    |--- /api/orchestration/* -> Orchestrator (multi-app coordination)
    |--- /api/v1/shares/*  --> Platform API (internal, Firestore)
    |--- /api/v1/public/shares/* -> Platform API (unauthenticated share access)
    |--- /api/v1/extract/* --> Platform API (document extraction pipeline)
    |--- /api/projects/v1/* -> Unified Projects API (cross-app project CRUD)
    |--- /api/user/profile --> Platform API (aggregated from Accounts + Firestore)
    |--- /submit, /status, /results, etc. --> Site DNA API (legacy root-level routes)
\`\`\`

---

## Service Registry

All backend service URLs are configured via environment variables with production defaults:

\`\`\`python
SERVICE_URLS = {
    'site_dna':     'https://vortx-opt-backend-202472576296.us-central1.run.app',
    'flux_ev':      'https://flux-ev-api-202472576296.us-central1.run.app',
    'flux_phd':     'https://flux-phd-api-202472576296.us-central1.run.app',
    'flux_ems':     'https://flux-ems-api-202472576296.us-central1.run.app',
    'cold_storage': 'https://flux-cold-storage-api-202472576296.us-central1.run.app',
    'orchestrator': 'https://vortx-orchestrator-202472576296.us-central1.run.app',
    'accounts':     'https://vortx-accounts-202472576296.us-central1.run.app',
}
\`\`\`

---

## Authentication Flow

### 1. Firebase Token Verification

Every authenticated request includes an \`Authorization: Bearer <firebase_id_token>\` header. The Platform API verifies this using Firebase Admin SDK:

\`\`\`
Frontend sends:  Authorization: Bearer <firebase_id_token>
                          |
                          v
Platform API:   verify_token(id_token)
                          |
                          v
                firebase_auth.verify_id_token(id_token)
                          |
                          v
                AuthenticatedUser(uid, email, email_verified, name, picture)
\`\`\`

**File:** \`apps/vortx-platform-api/services/auth.py\`

The \`AuthenticatedUser\` dataclass contains:
- \`uid\` -- Firebase user ID (primary identifier)
- \`email\` -- User email
- \`email_verified\` -- Whether email is verified
- \`name\` -- Display name (from OAuth provider)
- \`picture\` -- Avatar URL (from OAuth provider)

### 2. Auth Context Retrieval

After token verification, the Platform API fetches the user's full authorization context from the Accounts Service:

\`\`\`
Platform API:   get_auth_context(uid, auth_token)
                          |
                          v
                GET {accounts_url}/api/v1/auth/context
                    Authorization: Bearer <firebase_token>
                          |
                          v
Accounts Service returns:
{
    "user": {
        "id": "firebase_uid",
        "email": "user@example.com",
        "name": "User Name",
        "is_super_admin": false,
        "is_company_admin": false,
        "profile_completed": true
    },
    "company": {
        "id": "company_id",
        "name": "Company Name",
        "is_platform_owner": false
    },
    "products": {
        "site_dna": { "has_access": true, "plan": "professional", "permissions": {}, "add_ons": [] },
        "flux_ev":  { "has_access": false, "plan": null, "permissions": {}, "add_ons": [] },
        "flux_phd": { "has_access": false, "plan": null, "permissions": {}, "add_ons": [] },
        "flux_ems": { "has_access": false, "plan": null, "permissions": {}, "add_ons": [] }
    }
}
\`\`\`

**Auth Context Caching:** Auth contexts are cached in-memory for 60 seconds (configurable via \`AUTH_CACHE_TTL_SECONDS\`) to reduce load on the Accounts Service. The cache is safe because auth context rarely changes during a session.

**File:** \`apps/vortx-platform-api/services/auth_cache.py\`

### 3. Service Token Generation

The Platform API generates a signed JWT service token that downstream services can validate without re-verifying the Firebase token or re-calling the Accounts Service:

\`\`\`
Platform API:   generate_service_token(uid, auth_token)
                          |
                          v
                ServiceTokenManager.generate_token(auth_context)
                          |
                          v
                JWT signed with HS256 (5-minute TTL)
                Contains: user, company, admin flags, product access
\`\`\`

**JWT Claims:**
- \`iss\`: \`vortx-platform-api\`
- \`sub\`: Firebase UID
- \`iat\` / \`exp\`: Issued at / expiration (5-minute window)
- \`user\`: \`{uid, email, name}\`
- \`company\`: \`{id, name, is_platform_owner}\`
- \`admin\`: \`{is_super_admin, is_company_admin}\`
- \`products\`: Full product access map

**File:** \`apps/vortx-platform-api/services/service_token.py\`

**Secret:** \`SERVICE_TOKEN_SECRET\` environment variable (HS256 symmetric key).

### 4. The \`require_auth\` Decorator

The \`require_auth\` decorator in \`app.py\` orchestrates the full auth pipeline for every protected route:

1. Extract Bearer token from \`Authorization\` header
2. \`verify_token(token)\` -- Firebase token verification
3. \`get_auth_context(uid, token)\` -- Fetch (or cache-hit) auth context from Accounts
4. \`get_user_permissions(uid, token)\` -- Extract product access list
5. \`generate_service_token(uid, token)\` -- Create signed JWT for backends
6. Store all on Flask \`g\`: \`g.user\`, \`g.permissions\`, \`g.auth_context\`, \`g.service_token\`

### 5. Backend Request Headers

When proxying to backend services, the Platform API sends:

\`\`\`
Authorization:   <original Firebase token> (fallback auth)
X-User-ID:       <firebase_uid>
X-User-Email:    <user_email>
X-Service-Token: <signed JWT service token>
\`\`\`

Backend services can validate the \`X-Service-Token\` JWT to get full user context without re-verifying the Firebase token.

---

## Routing

### Generic Proxy Route

The primary routing mechanism is the generic proxy at \`/api/<app_name>/<path:endpoint>\`:

\`\`\`python
@app.route('/api/<app_name>/<path:endpoint>', methods=['GET', 'POST', 'PUT', 'DELETE', 'PATCH'])
@require_auth
@limiter.limit("2000 per hour")
def proxy_to_backend(app_name, endpoint):
\`\`\`

This route:
1. Checks that \`app_name\` is in the user's \`g.permissions\` list
2. Calls \`route_to_backend(app_name, endpoint)\` to proxy the request

### URL Construction Logic

The \`route_to_backend()\` function handles URL prefix differences between services:

| Service | URL Pattern | Example |
|---------|-------------|---------|
| \`site_dna\` (blueprint routes) | \`{url}/api/{endpoint}\` | \`/api/site_dna/v1/projects/123\` -> \`{site_dna_url}/api/v1/projects/123\` |
| \`site_dna\` (legacy root routes) | \`{url}/{endpoint}\` | \`/submit\` -> \`{site_dna_url}/submit\` |
| \`flux_ev\`, \`flux_phd\`, \`cold_storage\` | \`{url}/api/{endpoint}\` | \`/api/flux_ev/v1/configs\` -> \`{flux_ev_url}/api/v1/configs\` |
| \`flux_ems\` | \`{url}/api/flux_ems/{endpoint}\` | \`/api/flux_ems/runs\` -> \`{flux_ems_url}/api/flux_ems/runs\` |
| \`orchestrator\` | \`{url}/{endpoint}\` | \`/api/orchestration/runs\` -> \`{orchestrator_url}/optimize\` |

**Site DNA Legacy Root Endpoints** (served without \`/api/\` prefix):
\`\`\`
submit, schema, load_models, load_modeling, calculate_bau,
monthly_performance, validate_bill_calculation, find-minimum-limit,
rate-limit, health, urdb, status, results, financial_analysis, stop_polling
\`\`\`

### Multipart/Form-Data Support

The proxy handles file uploads by detecting \`multipart/form-data\` content type and forwarding files and form fields separately via the \`requests\` library.

---

## API Routes Reference

### Public Routes (No Authentication)

| Method | Path | Target | Description |
|--------|------|--------|-------------|
| GET | \`/health\` | Platform API | Health check (exempt from rate limits) |
| GET | \`/\` | Platform API | Root endpoint with service info |
| GET | \`/schema\` | Site DNA API | REopt input schema |
| GET | \`/api/tariffs\` | Site DNA API | Tariff lookup by address |
| GET | \`/status/<run_uuid>\` | Site DNA API | Optimization run status (polling) |
| GET | \`/results/<run_uuid>\` | Site DNA API | Optimization run results |
| POST | \`/calculate_bau\` | Site DNA API | BAU utility bill calculation |
| GET | \`/load_models\` | Site DNA API | Available load models |
| POST | \`/load_modeling\` | Site DNA API | Generate load profile from model |
| POST | \`/monthly_performance\` | Site DNA API | Monthly performance breakdown |
| POST | \`/validate_bill_calculation\` | Site DNA API | Bill calculation validation |
| GET | \`/financial_analysis/<run_uuid>\` | Site DNA API | Financial analysis for optimization run |
| POST | \`/stop_polling/<run_uuid>\` | Site DNA API | Stop polling for an optimization |
| GET | \`/urdb\` | Site DNA API | URDB tariff search |
| GET | \`/urdb/<label>\` | Site DNA API | URDB tariff by label |
| GET | \`/rate-limit\` | Site DNA API | Rate limit status |
| POST | \`/find-minimum-limit\` | Site DNA API | Minimum grid import limit |
| GET | \`/api/v1/public/shares/<id>/meta\` | Platform API | Public share metadata (safe info only) |
| POST | \`/api/v1/public/shares/<id>/access\` | Platform API | Validate share credentials, issue token |
| GET | \`/api/v1/public/shares/<id>/data\` | Platform API | Full share data (requires access token) |
| POST | \`/api/v1/public/shares/<id>/download\` | Platform API | Record download event (requires access token) |

### Authenticated Routes -- Generic Proxy

| Method | Path | Target | Description |
|--------|------|--------|-------------|
| ALL | \`/api/<app_name>/<path:endpoint>\` | Varies | Generic proxy to backend services (rate limited: 2000/hr) |
| POST | \`/submit\` | Site DNA API | Submit optimization (rate limited: 20/hr) |

### Authenticated Routes -- Admin

| Method | Path | Target | Description |
|--------|------|--------|-------------|
| ALL | \`/api/v1/admin/companies/*\` | Accounts Service | Company CRUD (super admin) |
| ALL | \`/api/v1/admin/users/*\` | Accounts Service | User management (super admin) |
| ALL | \`/api/v1/admin/my-company/*\` | Accounts Service | Company self-management |
| ALL | \`/api/v1/admin/invites/*\` | Accounts Service | Invite management |
| ALL | \`/api/v1/admin/tariffs/*\` | Site DNA API | Tariff admin |
| ALL | \`/api/v1/admin/equipment/*\` | Site DNA API | Equipment admin |
| ALL | \`/api/v1/admin/capex/*\` | Site DNA API | CAPEX admin |
| ALL | \`/api/v1/admin/opex/*\` | Site DNA API | OPEX admin |
| ALL | \`/api/v1/admin/solver/*\` | Site DNA API | Solver monitoring |
| ALL | \`/api/v1/admin/solver-stats\` | Site DNA API | Solver statistics |
| ALL | \`/api/v1/admin/solver-comparisons\` | Site DNA API | Solver comparisons |
| ALL | \`/api/v1/admin/bug-reports/*\` | Redirect | Redirects to \`/api/v1/bug-reports/admin/*\` |

### Authenticated Routes -- Billing

| Method | Path | Target | Description |
|--------|------|--------|-------------|
| ALL | \`/api/v1/billing/<path:endpoint>\` | Accounts Service | Billing (Stripe) operations |

### Authenticated Routes -- Simulation

| Method | Path | Target | Description |
|--------|------|--------|-------------|
| ALL | \`/api/simulation/<path:endpoint>\` | Flux EMS API | Simulation run management (legacy route) |

### Authenticated Routes -- User Profile & Permissions

| Method | Path | Target | Description |

---
*This page is auto-generated from \`docs/architecture/\`. See the full document for complete details.*
`;

export default content;
